import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import {
  AccountType,
  AddOnRateType,
  AddOnType,
  AutoAddOnAssignmentSourceType,
  getV1CompaniesCompanyIdSettlementsId,
  postV1CompaniesCompanyIdAccountsAccountIdAutoAddOnAssignments,
  postV1CompaniesCompanyIdDriversDriverIdAutoAddOnAssignments,
  postV1CompaniesCompanyIdSettlementsSettlementIdRebill,
  SettlementLineItemType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Drawer } from '~components/Drawer/src/ui';
import { Body } from '~components/Drawer/src/ui/Body';
import { Footer } from '~components/Drawer/src/ui/Footer';
import { Header } from '~components/Drawer/src/ui/Header';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import useSettlementLineItems from '~hooks/useSettlementLineItems/useSettlementLineItems';
import { Settlement } from '~hooks/useSettlements/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import CreateAddonsConfirmationDialog from './RebillSettlement/CreateAddonsConfirmationDialog';
import RebillSettlementTotals from './RebillSettlement/RebillSettlementTotals';
import SkipAddonsConfirmationDialog from './RebillSettlement/SkipAddonsConfirmationDialog';
import { convertSettlementLineItemToDTO } from './RebillSettlement/VendorDriverAdjustmentRow';
import VendorDriverAdjustmentsForm, {
  VendorDriverAdjustmentsFormData,
  VendorDriverAdjustmentsFormRef,
} from './RebillSettlement/VendorDriverAdjustmentsForm';
import VoidRebillCompletionSummary from './RebillSettlement/VoidRebillCompletionSummary';
import RebillSettlementForm from './RebillSettlementForm';
import VoidSettlementForm from './VoidSettlementForm';

type VoidMode = 'void' | 'void-rebill';
type VoidRebillStep = 'void' | 'rebill' | 'adjustments' | 'done';

interface VoidSettlementFormData {
  reason: string;
}

interface VoidSettlementDrawerProps {
  open: boolean;
  onClose: () => void;
  onSettlementChange: (settlement: Settlement) => void;
  onVoid: (reason: string) => Promise<void>;
  settlement?: Settlement;
  mode?: VoidMode;
}

export default function VoidSettlementDrawer({
  open,
  onClose,
  onSettlementChange,
  onVoid,
  settlement,
  mode = 'void',
}: VoidSettlementDrawerProps) {
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voidRebillStep, setVoidRebillStep] = useState<VoidRebillStep>('void');
  const [rebillSettlement, setRebillSettlement] = useState<Settlement | undefined>();
  const [showSkipAddonsDialog, setShowSkipAddonsDialog] = useState(false);
  const [showCreateAddonsDialog, setShowCreateAddonsDialog] = useState(false);
  const [formData, setFormData] = useState<VendorDriverAdjustmentsFormData | undefined>();

  const vendorDriverAdjustmentsFormRef = useRef<VendorDriverAdjustmentsFormRef>(null);
  const { getAllSettlementLineItems } = useSettlementLineItems();

  const handleRatesUpdated = useCallback(async () => {
    if (rebillSettlement?.id) {
      try {
        // Fetch the updated settlement data directly from the API
        const response = await getV1CompaniesCompanyIdSettlementsId({
          path: { 'company-id': companyId, id: rebillSettlement.id },
        });
        const updatedSettlement = Settlement.parse(response.data.data);
        setRebillSettlement(updatedSettlement);
        onSettlementChange(updatedSettlement);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_rebill_settlement'),
        );
      }
    }
  }, [companyId, onSettlementChange, rebillSettlement?.id]);

  useEffect(() => {
    if (isSubmitting) return;
    setVoidRebillStep('void');
    setRebillSettlement(undefined);
    setFormData(undefined);
  }, [settlement]);

  const onVoidSubmit = async (data: VoidSettlementFormData) => {
    setIsSubmitting(true);

    // For void mode, proceed with void action
    // errors are handled in the onVoid function
    try {
      await onVoid(data.reason.trim());
    } catch (error) {
      console.error('Failed to void settlement:', error);
    }

    if (mode === 'void-rebill') {
      try {
        // For void-rebill mode, call the rebill endpoint and store the returned settlement
        if (settlement?.id) {
          const response = await postV1CompaniesCompanyIdSettlementsSettlementIdRebill({
            path: {
              'settlement-id': String(settlement.id),
              'company-id': companyId,
            },
          });
          const rebillSettlementData = Settlement.parse(response.data.data);
          setRebillSettlement(rebillSettlementData);
          setVoidRebillStep('rebill');
        }
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_rebill_settlement'),
        );
      } finally {
        setIsSubmitting(false);
      }
    } else {
      handleClose();
    }

    setIsSubmitting(false);
  };

  const onRebillSubmit = async () => {
    if (isSubmitting) return;

    if (!rebillSettlement?.id) {
      setVoidRebillStep('adjustments');
      return;
    }

    setIsSubmitting(true);
    try {
      const lineItemsRequest = await getAllSettlementLineItems(
        companyId,
        String(rebillSettlement.id),
        SettlementLineItemType.LOAD,
      );
      const lineItems = lineItemsRequest.map((group) => group.lineItems).flat();

      const adjustmentDTOs = lineItems
        .map((lineItem, index) => convertSettlementLineItemToDTO(lineItem, index))
        .filter((dto) => dto.adjustmentTotal !== 0);

      // Only skip to 'done' if we have positive evidence of success (non-empty lineItems)
      // and all adjustments are zero. If lineItems is empty, it could indicate an API
      // failure (hook returns [] on error), so default to showing adjustments screen.
      if (lineItems.length > 0 && adjustmentDTOs.length === 0) {
        setVoidRebillStep('done');
      } else {
        setVoidRebillStep('adjustments');
      }
    } catch (error) {
      console.error('Failed to check for adjustments:', error);
      setVoidRebillStep('adjustments');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipAddons = () => {
    setShowSkipAddonsDialog(true);
  };

  const handleConfirmSkipAddons = () => {
    setShowSkipAddonsDialog(false);
    handleClose();
  };

  const handleCancelSkipAddons = () => {
    setShowSkipAddonsDialog(false);
  };

  const handleCreateAddons = async () => {
    if (vendorDriverAdjustmentsFormRef.current) {
      try {
        const data = await vendorDriverAdjustmentsFormRef.current.submit();
        if (data) {
          setFormData(data);
          setShowCreateAddonsDialog(true);
        }
      } catch (error) {
        console.error('Form validation failed:', error);
      }
    }
  };

  const handleConfirmCreateAddons = async () => {
    setShowCreateAddonsDialog(false);
    setIsSubmitting(true);

    try {
      if (formData?.adjustments && formData.adjustments.length > 0) {
        // Process each adjustment that has adjustPay set to true
        const addOnPromises = formData.adjustments
          .filter(
            (adjustment) =>
              adjustment.adjustPay &&
              adjustment.adjustmentTotal !== 0 &&
              adjustment.adjustmentTotal !== null,
          )
          .map(async (adjustment) => {
            const commonBody = {
              name: adjustment.addOnDescription || 'Settlement Adjustment',
              quantity: 1,
              revenue_rate_value: adjustment.adjustmentTotal,
              cost_rate_value: 0,
              add_on_rate_type: AddOnRateType.RATE_FOR_EACH,
              add_on_types: [AddOnType.SETTLEMENT],
              total_installments: 1,
              source_id: rebillSettlement?.id ?? null,
              source_type: rebillSettlement?.id
                ? AutoAddOnAssignmentSourceType.SETTLEMENT
                : null,
            };

            // If vendor exists, use vendor account, otherwise use driver
            if (adjustment.vendor?.id) {
              await postV1CompaniesCompanyIdAccountsAccountIdAutoAddOnAssignments({
                path: { 'company-id': companyId, 'account-id': adjustment.vendor.id },
                body: {
                  ...commonBody,
                  account_type: AccountType.VENDOR,
                },
              });
            } else if (adjustment.driver?.id) {
              await postV1CompaniesCompanyIdDriversDriverIdAutoAddOnAssignments({
                path: { 'company-id': companyId, 'driver-id': adjustment.driver.id },
                body: commonBody,
              });
            }
          });

        await Promise.all(addOnPromises);

        toasterStore.push(
          alert(
            t(
              'approvals.driver_pay.settlements.rebill_adjustments.add_ons_created_successfully',
            ),
            AlertTypes.success,
          ),
        );
      }
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.settlements.failed_to_create_addons'),
      );
      console.error('Failed to create addons:', error);
    } finally {
      setIsSubmitting(false);
      setVoidRebillStep('done');
    }
  };

  const handleCancelCreateAddons = () => {
    setShowCreateAddonsDialog(false);
    setFormData(undefined);
  };

  const handleClose = () => {
    onClose();
    setVoidRebillStep('void');
    setRebillSettlement(undefined);
    setFormData(undefined);
  };

  const getTitle = () => {
    if (mode === 'void-rebill' && voidRebillStep === 'done') {
      return t('approvals.driver_pay.settlements.void_rebill_completion.title');
    }
    if (mode === 'void-rebill' && voidRebillStep === 'rebill') {
      return t('approvals.driver_pay.settlements.void_rebill_settlement.rebill_title', {
        settlementId: rebillSettlement?.settlementId || settlement?.settlementId || '',
      });
    }
    if (mode === 'void-rebill' && voidRebillStep === 'adjustments') {
      return t('approvals.driver_pay.settlements.rebill_adjustments.title', {
        settlementId: rebillSettlement?.settlementId || settlement?.settlementId || '',
      });
    }
    if (mode === 'void-rebill') {
      return t('approvals.driver_pay.settlements.void_rebill_settlement.void_title', {
        settlementId: settlement?.settlementId || '',
      });
    }
    return t('approvals.driver_pay.settlements.void_settlement.title', {
      settlementId: settlement?.settlementId || '',
    });
  };

  const getButtonText = () => {
    if (mode === 'void-rebill' && voidRebillStep === 'rebill') {
      return t(
        'approvals.driver_pay.settlements.void_rebill_settlement.save_adjustments',
      );
    }
    if (mode === 'void-rebill' && voidRebillStep === 'adjustments') {
      return t('approvals.driver_pay.settlements.rebill_adjustments.title');
    }
    if (mode === 'void-rebill') {
      return t(
        'approvals.driver_pay.settlements.void_rebill_settlement.continue_button_text',
      );
    }
    return t('approvals.driver_pay.settlements.void_settlement.void_settlement');
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      anchor="right"
      variant="temporary"
      sx={{
        '& .MuiDrawer-paper': {
          width: '80%',
        },
      }}
    >
      <Header title={getTitle()}>
        <IconButton onClick={() => onClose()}>
          <Close />
        </IconButton>
      </Header>

      <Body>
        <Box sx={{ p: 2 }}>
          {mode === 'void-rebill' && voidRebillStep === 'rebill' ? (
            <RebillSettlementForm
              settlement={rebillSettlement || settlement}
              onSubmit={onRebillSubmit}
              onRatesUpdated={handleRatesUpdated}
            />
          ) : mode === 'void-rebill' && voidRebillStep === 'adjustments' ? (
            <VendorDriverAdjustmentsForm
              ref={vendorDriverAdjustmentsFormRef}
              settlement={rebillSettlement || settlement}
            />
          ) : mode === 'void-rebill' && voidRebillStep === 'done' ? (
            <VoidRebillCompletionSummary
              originalSettlementId={settlement?.settlementId || ''}
              rebillSettlementId={rebillSettlement?.settlementId || ''}
            />
          ) : (
            <VoidSettlementForm
              settlement={settlement}
              isSubmitting={isSubmitting}
              onSubmit={onVoidSubmit}
              mode={mode}
            />
          )}
        </Box>
      </Body>

      <Footer>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 2, marginLeft: 'auto' }}>
            {mode === 'void-rebill' &&
              voidRebillStep === 'rebill' &&
              rebillSettlement && (
                <>
                  <RebillSettlementTotals
                    oldTotal={rebillSettlement.totalAmount}
                    newTotal={rebillSettlement.rebillTotalAmount ?? 0}
                  />
                  <Divider orientation="vertical" flexItem />
                </>
              )}
            {mode === 'void-rebill' && voidRebillStep === 'adjustments' ? (
              <>
                <SmallButton
                  variant="outlined"
                  color="secondary"
                  onClick={handleSkipAddons}
                >
                  {t('approvals.driver_pay.settlements.rebill_adjustments.skip_addons')}
                </SmallButton>
                <SmallLoadingButton
                  loading={isSubmitting}
                  variant="contained"
                  color="primary"
                  onClick={handleCreateAddons}
                >
                  {t('approvals.driver_pay.settlements.rebill_adjustments.create_addons')}
                </SmallLoadingButton>
              </>
            ) : mode === 'void-rebill' && voidRebillStep === 'done' ? (
              <SmallLoadingButton
                loading={isSubmitting}
                variant="contained"
                color="primary"
                onClick={handleClose}
              >
                {t(
                  'approvals.driver_pay.settlements.void_rebill_completion.close_button',
                )}
              </SmallLoadingButton>
            ) : (
              <>
                <SmallButton variant="outlined" color="secondary" onClick={handleClose}>
                  {t('actions.cancel')}
                </SmallButton>
                <SmallLoadingButton
                  loading={isSubmitting}
                  variant="contained"
                  color="primary"
                  type="submit"
                  form={
                    mode === 'void-rebill' && voidRebillStep === 'rebill'
                      ? 'rebill-settlement-form'
                      : 'void-settlement-form'
                  }
                >
                  {getButtonText()}
                </SmallLoadingButton>
              </>
            )}
          </Box>
        </Box>
      </Footer>

      <SkipAddonsConfirmationDialog
        open={showSkipAddonsDialog}
        onClose={handleCancelSkipAddons}
        onConfirm={handleConfirmSkipAddons}
      />
      <CreateAddonsConfirmationDialog
        open={showCreateAddonsDialog}
        onClose={handleCancelCreateAddons}
        onConfirm={handleConfirmCreateAddons}
      />
    </Drawer>
  );
}
